import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container, Typography, Box, Paper, Divider, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Tooltip, Avatar, TextField,
    Button, Card, CardContent, Grid as Grid, Link
} from '@mui/material';
import {
    History, Notifications, BarChart, CloudUpload, Send, Delete,
    Block, People, Comment as CommentIcon, Link as LinkIcon, Star, Edit, Campaign
} from '@mui/icons-material';
import jsPDF from "jspdf";

export default function BroadcastPage() {
    const [history, setHistory] = useState<any[]>([]);
    const [subscribers, setSubscribers] = useState<any[]>([]);
    const [comments, setComments] = useState<any[]>([]);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [isSending, setIsSending] = useState(false);

    const poppins = { fontFamily: "'Poppins', sans-serif" };

    const fetchData = async () => {
        try {
            const [subRes, comRes, notifRes] = await Promise.all([
                axios.get('https://api.237showbiz.com/api/subscribers'),
                axios.get('https://api.237showbiz.com/api/comments'),
                axios.get('https://api.237showbiz.com/api/Notifications?subscriber_id=admin')
            ]);
            setSubscribers(subRes.data || []);
            setComments(comRes.data || []);
            setHistory(notifRes.data.notifications || []);
        } catch (err) { console.error(err); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            setImage(e.target.files[0]);
            setPreview(URL.createObjectURL(e.target.files[0]));
        }
    };

    const sendNotification = async () => {
        setIsSending(true);
        const formData = new FormData();
        formData.append('title', title);
        formData.append('body', body);
        if (image) formData.append('image', image);
        try {
            await axios.post('https://api.237showbiz.com/api/Notifications?action=send', formData);
            setTitle(''); setBody(''); setImage(null); setPreview(null);
            fetchData();
        } catch (err) { alert("Failed to send"); } finally { setIsSending(false); }
    };

    const downloadNotificationPDF = (notif: any) => {
        const doc = new jsPDF();
        doc.text(`Notification: ${notif.title}`, 14, 20);
        doc.text(`Clicks: ${notif.click_count}`, 14, 30);
        doc.save(`notif-${notif.notification_id}.pdf`);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 6, mb: 10 }}>
            <Grid container spacing={4}>
                {/* 1. BROADCAST FORM */}
                <Grid size={{ xs: 12 }}>
                    <Card elevation={0} sx={{ borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', border: '1px solid #FFF' }}>
                        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, bgcolor: '#FFF7ED', borderRadius: '12px' }}>
                                    <Send sx={{ color: 'orange' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1B2559', mb: 0.5, ...poppins }}>
                                        Push Broadcast
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#A3AED0', ...poppins }}>
                                        Send instant notifications with real-time tracking.
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <TextField
                                        fullWidth label="Notification Title" variant="filled" sx={{ mb: 3 }}
                                        value={title} onChange={(e) => setTitle(e.target.value)}
                                        InputProps={{ disableUnderline: true, sx: { borderRadius: '15px', bgcolor: '#F4F7FE', ...poppins } }}
                                    />
                                    <TextField
                                        fullWidth label="Message Body" variant="filled" multiline rows={5}
                                        value={body} onChange={(e) => setBody(e.target.value)}
                                        InputProps={{ disableUnderline: true, sx: { borderRadius: '15px', bgcolor: '#F4F7FE', ...poppins } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <Box sx={{
                                        border: '2px dashed #E0E5F2', borderRadius: '20px', height: '100%',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        justifyContent: 'center', p: 3, bgcolor: '#F4F7FE', minHeight: 240,
                                        transition: 'all 0.3s ease', '&:hover': { borderColor: 'orange', bgcolor: '#FFF' }
                                    }}>
                                        {!preview ? (
                                            <Button variant="text" component="label" startIcon={<CloudUpload />} sx={{ ...poppins, textTransform: 'none', color: '#707EAE', fontWeight: 600 }}>
                                                Upload Media Asset
                                                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                            </Button>
                                        ) : (
                                            <Box textAlign="center" sx={{ width: '100%' }}>
                                                <img src={preview} alt="preview" style={{ width: '100%', maxHeight: 180, borderRadius: '16px', objectFit: 'contain' }} />
                                                <Button size="small" color="error" variant="text" onClick={() => {setImage(null); setPreview(null);}} sx={{ mt: 1, fontWeight: 600, ...poppins }}>
                                                    Remove Media
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>

                            <Button
                                fullWidth variant="contained" size="large" startIcon={<Send />}
                                disabled={isSending} onClick={sendNotification}
                                sx={{
                                    mt: 10, bgcolor: 'orange', py: 2, borderRadius: '15px',
                                    fontWeight: 700, fontSize: '1rem', textTransform: 'none',
                                    boxShadow: '0 12px 24px rgba(255, 165, 0, 0.3)',
                                    '&:hover': { bgcolor: '#FB923C' },
                                    ...poppins
                                }}
                            >
                                {isSending ? 'Dispatching...' : 'Dispatch Broadcast'}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {/* HISTORY TABLE TRACKING SECTION */}
                <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                        <Box p={4} display="flex" alignItems="center" gap={2}>
                            <Box sx={{ p: 1, bgcolor: '#F0F9FF', borderRadius: '10px' }}>
                                <History sx={{ color: '#0EA5E9' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>Broadcast History (Last 48h)</Typography>
                        </Box>
                        <Divider />
                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>NOTIFICATION</TableCell>
                                        <TableCell align="center" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>CLICKS</TableCell>
                                        <TableCell align="center" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>SENT AT</TableCell>
                                        <TableCell align="right" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>ACTION</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.length === 0 ? (
                                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: '#A3AED0', ...poppins }}>No recent broadcasts found.</TableCell></TableRow>
                                    ) : (
                                        history.map((notif) => (
                                            <TableRow key={notif.notification_id} hover>
                                                <TableCell sx={poppins}>
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        {notif.image_url ? (
                                                            <Avatar src={notif.image_url} variant="rounded" sx={{ width: 45, height: 45, borderRadius: '10px' }} />
                                                        ) : (
                                                            <Avatar variant="rounded" sx={{ width: 45, height: 45, borderRadius: '10px', bgcolor: '#F4F7FE' }}><Notifications sx={{ color: '#A3AED0' }} /></Avatar>
                                                        )}
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1B2559' }}>{notif.title}</Typography>
                                                            <Typography variant="caption" sx={{ color: '#A3AED0' }}>{notif.body.substring(0, 45)}...</Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Total User Clicks">
                                                        <Box sx={{ bgcolor: '#FFF7ED', color: 'orange', px: 1.5, py: 0.5, borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                                                            <BarChart fontSize="small" /> {notif.click_count}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: '#707EAE', fontSize: '0.85rem', ...poppins }}>
                                                    {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => downloadNotificationPDF(notif)}
                                                        sx={{ bgcolor: '#EEF2FF', mr: 1 }}
                                                    >
                                                        <CloudUpload fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* SUBSCRIBERS LOG MANAGEMENT LISTS */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                        <Box p={4} display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={2}>
                                <People sx={{ color: 'orange' }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>Subscribers</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ bgcolor: '#F4F7FE', color: '#4318FF', px: 2, py: 0.5, borderRadius: '8px', fontWeight: 700 }}>
                                {subscribers.length} Users
                            </Typography>
                        </Box>
                        <Divider />
                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>IDENTITY</TableCell>
                                        <TableCell align="right" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>MANAGE</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subscribers.map((sub) => (
                                        <TableRow key={sub.id} hover>
                                            <TableCell sx={poppins}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1B2559' }}>{sub.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{sub.email}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" sx={{ mr: 1 }}><Block fontSize="small" color={sub.banned ? "error" : "disabled"} /></IconButton>
                                                <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* USER FEEDBACK DISCUSSION COMMENT GROUPS */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                        <Box p={4} display="flex" alignItems="center" gap={2}>
                            <CommentIcon sx={{ color: 'orange' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>User Feedback</Typography>
                        </Box>
                        <Divider />
                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>CONTENT</TableCell>
                                        <TableCell align="right" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>ACTION</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {comments.map((com) => (
                                        <TableRow key={com.comment_id} hover>
                                            <TableCell sx={poppins}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1B2559' }}>{com.subscriber_name}</Typography>
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#707EAE' }}>{com.text}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}